import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Send,
  Plus,
  Trash2,
  MessageCircle,
  Sparkles,
  Loader2,
  ChevronLeft,
  Menu,
  X,
} from "lucide-react";
import { toast } from "sonner";

const ChatPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isPremium = user?.is_premium;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isPremium) {
      fetchSessions();
    } else {
      setLoadingSessions(false);
    }
  }, [isPremium]);

  const fetchSessions = async () => {
    try {
      const res = await api.get("/chat/sessions");
      setSessions(res.data);
    } catch (err) {
      console.error("Error fetching sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    setSidebarOpen(false);
    try {
      const res = await api.get(`/chat/sessions/${sessionId}/messages`);
      setMessages(res.data);
    } catch (err) {
      console.error("Error loading messages:", err);
      toast.error("Failed to load conversation");
    }
  };

  const startNewChat = () => {
    setActiveSessionId(null);
    setMessages([]);
    setInput("");
    setSidebarOpen(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${sessionId}`);
      setSessions((prev) => prev.filter((s) => s.chat_session_id !== sessionId));
      if (activeSessionId === sessionId) {
        startNewChat();
      }
      toast.success("Conversation deleted");
    } catch (err) {
      toast.error("Failed to delete conversation");
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setInput("");
    setSending(true);

    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const res = await api.post("/chat/send", {
        message: text,
        session_id: activeSessionId,
      });

      const { session_id, ai_message } = res.data;

      if (!activeSessionId) {
        setActiveSessionId(session_id);
        fetchSessions();
      }

      setMessages((prev) => {
        const updated = prev.map((m) =>
          m.message_id === tempUserMsg.message_id ? res.data.user_message : m
        );
        return [...updated, ai_message];
      });
    } catch (err) {
      console.error("Error sending message:", err);
      const errorMsg = err.response?.status === 403
        ? "Premium feature — upgrade to access AI Chat Advisor"
        : "Failed to send message. Please try again.";
      toast.error(errorMsg);
      setMessages((prev) => prev.filter((m) => m.message_id !== tempUserMsg.message_id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const suggestedTopics = [
    "What does my birth chart say about my love life?",
    "I'm feeling lost in my career. Can you guide me?",
    "What spiritual practices would benefit me right now?",
    "How can I improve my relationships?",
  ];

  // Premium gate
  if (!isPremium) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#070B14" }}>
        <div className="text-center max-w-md space-y-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B5CF6] flex items-center justify-center mx-auto">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-display text-3xl text-white">AI Chat Advisor</h1>
          <p className="text-white/60 font-body">
            Unlock your personal spiritual mentor. Get personalized guidance on relationships, career, spirituality, and more.
          </p>
          <Button
            data-testid="chat-upgrade-btn"
            onClick={() => navigate("/premium")}
            className="btn-gold text-lg px-8 py-3"
          >
            Upgrade to Premium
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-white/50 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex" style={{ background: "#070B14" }} data-testid="chat-page">
      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-[#0A101D] border-r border-white/5 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="chat-sidebar"
      >
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg text-white">Conversations</h2>
            <button
              className="lg:hidden p-1 text-white/50 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <Button
            data-testid="new-chat-btn"
            onClick={startNewChat}
            className="w-full btn-gold"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loadingSessions ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-center text-white/30 text-sm py-8 font-body">No conversations yet</p>
          ) : (
            sessions.map((s) => (
              <button
                key={s.chat_session_id}
                data-testid={`session-${s.chat_session_id}`}
                onClick={() => loadSession(s.chat_session_id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 group transition-colors ${
                  activeSessionId === s.chat_session_id
                    ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "text-white/60 hover:bg-white/5 hover:text-white/80"
                }`}
              >
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate text-sm font-body">{s.title}</span>
                <button
                  onClick={(e) => deleteSession(e, s.chat_session_id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-white/30 hover:text-red-400 transition-opacity"
                  data-testid={`delete-session-${s.chat_session_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </button>
            ))
          )}
        </div>
        <div className="p-4 border-t border-white/5">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="w-full text-white/50 hover:text-white hover:bg-white/5 justify-start"
            data-testid="chat-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <header className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-white/5 bg-[#070B14]/80 backdrop-blur-xl">
          <button
            className="lg:hidden p-2 text-white/50 hover:text-white"
            onClick={() => setSidebarOpen(true)}
            data-testid="chat-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#D4AF37] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-display text-base text-white">SoulGuide Advisor</h1>
            <p className="text-xs text-white/40 font-body">Your spiritual AI mentor</p>
          </div>
        </header>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-6" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-12">
              <div className="space-y-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8B5CF6]/20 to-[#D4AF37]/20 border border-white/10 flex items-center justify-center mx-auto">
                  <Sparkles className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h2 className="font-display text-xl text-white">Ask Your Advisor</h2>
                <p className="text-white/40 font-body text-sm max-w-sm">
                  Get personalized spiritual guidance on relationships, career, growth, and more.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg w-full">
                {suggestedTopics.map((topic, idx) => (
                  <button
                    key={idx}
                    data-testid={`suggested-topic-${idx}`}
                    onClick={() => {
                      setInput(topic);
                      inputRef.current?.focus();
                    }}
                    className="text-left px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white/70 text-sm font-body hover:bg-white/10 hover:text-white transition-colors"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`message-${msg.message_id}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[70%] ${
                    msg.role === "user"
                      ? "bg-[#D4AF37]/15 border border-[#D4AF37]/20 rounded-2xl rounded-br-md"
                      : "bg-white/5 border border-white/10 rounded-2xl rounded-bl-md"
                  } px-4 py-3`}
                >
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6]" />
                      <span className="text-xs text-[#8B5CF6] font-body">SoulGuide Advisor</span>
                    </div>
                  )}
                  <p className="text-white/85 font-body text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </p>
                  <p className="text-white/25 text-xs mt-2 font-body">
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6]" />
                  <span className="text-xs text-[#8B5CF6] font-body">SoulGuide Advisor</span>
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="w-2 h-2 rounded-full bg-[#8B5CF6]/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 rounded-full bg-[#8B5CF6]/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 rounded-full bg-[#8B5CF6]/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-white/5 px-4 sm:px-6 py-4 bg-[#070B14]/80 backdrop-blur-xl">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            <textarea
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask your spiritual advisor..."
              rows={1}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50 resize-none min-h-[44px] max-h-[120px]"
              style={{ height: "auto" }}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
              }}
            />
            <Button
              data-testid="send-btn"
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="btn-gold h-11 w-11 p-0 flex-shrink-0 rounded-xl"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-center text-white/20 text-xs mt-2 font-body">
            SoulGuide AI provides spiritual guidance for inspiration only. Not professional advice.
          </p>
        </div>
      </main>
    </div>
  );
};

export default ChatPage;
